import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { QrCode, Shield, Clock } from 'lucide-react';

const PaymentMethodModal = ({ 
  isOpen, 
  onClose, 
  baseAmount, 
  packageName,
  onConfirm 
}) => {
  const [processingFee, setProcessingFee] = useState(0);
  const [totalAmount, setTotalAmount] = useState(baseAmount);

  // PayMongo QRPH fee is 1.5%
  useEffect(() => {
    const fee = baseAmount * 0.015; // 1.5%
    setProcessingFee(fee);
    setTotalAmount(baseAmount + fee);
  }, [baseAmount]);

  const handleConfirm = () => {
    onConfirm({
      paymentChannel: 'qrph',
      processingFee,
      totalAmount
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-blue-600" />
            Pay with QR Ph
          </DialogTitle>
          <DialogDescription>
            Scan the QR code with any banking app to complete your payment
          </DialogDescription>
        </DialogHeader>

        {/* Package Info */}
        <div className="bg-slate-50 p-4 rounded-lg mb-4">
          <p className="text-sm text-slate-600">You are purchasing:</p>
          <p className="font-semibold text-lg">{packageName}</p>
        </div>

        {/* Payment Method Info */}
        <div className="border-2 border-blue-100 bg-blue-50 p-4 rounded-lg mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-600 p-2 rounded-lg">
              <QrCode className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-blue-900">QR Ph Payment</p>
              <p className="text-sm text-blue-700">Scan with any bank app</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-blue-700 mb-2">
            <Shield className="h-4 w-4" />
            <span>Secure payment powered by PayMongo</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Clock className="h-4 w-4" />
            <span>Instant credit after payment</span>
          </div>
        </div>

        {/* Supported Banks */}
        <div className="mb-4">
          <p className="text-xs text-slate-500 mb-2">Works with all QR Ph enabled banks:</p>
          <p className="text-xs text-slate-600">
            GCash • Maya • BDO • BPI • UnionBank • Metrobank • RCBC • Security Bank • and more
          </p>
        </div>

        {/* Total Calculation */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Package Price:</span>
              <span>₱{baseAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm text-blue-600">
              <span>Processing Fee (1.5%):</span>
              <span>+₱{processingFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t-2 border-blue-200 pt-2">
              <span>Total Amount:</span>
              <span className="text-blue-600">₱{totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <QrCode className="h-4 w-4 mr-2" />
            Generate QR Code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodModal;
